import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { supabase } from '@/lib/supabase';
import { allNewsData } from '@/mocks/newsData';

interface NewsItem {
  id: string | number;
  title: string;
  category: string;
  summary?: string;
  excerpt?: string;
  cover_image?: string;
  image?: string;
  published_at?: string;
  date?: string;
  view_count?: number;
  views?: number | string;
  pinned?: boolean;
}

export default function NewsSection() {
  const [activeCategory, setActiveCategory] = useState('全部');
  const [newsList, setNewsList] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ once: true });
  const navigate = useNavigate();
  const categories = ['全部', '公司新闻', '行业动态', '技术分享'];

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const { data, error } = await supabase
          .from('cms_news')
          .select('id,title,category,summary,cover_image,published_at,view_count,pinned')
          .eq('is_published', true)
          .order('pinned', { ascending: false })
          .order('published_at', { ascending: false })
          .limit(9);
        if (error) throw error;
        if (data && data.length > 0) {
          setNewsList(data);
        } else {
          setNewsList(allNewsData.map((n) => ({
            ...n,
            cover_image: n.image,
            summary: n.excerpt,
            view_count: parseInt(String(n.views).replace(/[^0-9]/g, '')) || 0,
          })));
        }
      } catch {
        setNewsList(allNewsData.map((n) => ({
          ...n,
          cover_image: n.image,
          summary: n.excerpt,
          view_count: parseInt(String(n.views).replace(/[^0-9]/g, '')) || 0,
        })));
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  const filteredNews = useMemo(() => {
    if (activeCategory === '全部') return newsList;
    return newsList.filter((item) => item.category === activeCategory);
  }, [activeCategory, newsList]);

  const featuredNews = filteredNews[0];
  const sideNews = filteredNews.slice(1);

  const getImage = (item: NewsItem) => item.cover_image || item.image || '';
  const getExcerpt = (item: NewsItem) => item.summary || item.excerpt || '';
  const getDate = (item: NewsItem) => {
    if (item.published_at) return new Date(item.published_at).toLocaleDateString('zh-CN').replace(/\//g, '-');
    return (item.date as string) || '';
  };
  const getViews = (item: NewsItem) => {
    const v = item.view_count ?? item.views;
    if (typeof v === 'number') return v.toLocaleString();
    return String(v ?? 0);
  };
  const getId = (item: NewsItem) => String(item.id);

  return (
    <section id="news" className="py-10 md:py-20 lg:py-24 bg-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-6 lg:px-8">
        {/* 标题区 */}
        <div
          ref={headerRef}
          className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 md:mb-12 gap-3 md:gap-6"
          style={{
            transition: 'opacity 0.7s ease, transform 0.7s ease',
            opacity: headerVisible ? 1 : 0.2,
            transform: headerVisible ? 'translateY(0)' : 'translateY(24px)',
          }}
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">新闻动态</h2>
          <div className="flex gap-2 flex-nowrap overflow-x-auto w-full lg:w-auto pb-1 lg:pb-0">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer flex-shrink-0 ${
                  activeCategory === category ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* 加载骨架 */}
        {loading && (
          <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
            <div className="lg:col-span-2 bg-white rounded-xl md:rounded-2xl h-64 animate-pulse"></div>
            <div className="space-y-4">
              <div className="bg-white rounded-xl h-28 animate-pulse"></div>
              <div className="bg-white rounded-xl h-28 animate-pulse"></div>
            </div>
          </div>
        )}

        {/* 内容区 */}
        {!loading && featuredNews && (
          <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
            {/* 主推文章 */}
            <div className="lg:col-span-2">
              <div
                onClick={() => navigate(`/news/${getId(featuredNews)}`)}
                className="bg-white rounded-xl md:rounded-2xl overflow-hidden hover:shadow-xl transition-shadow duration-300 cursor-pointer group h-full"
              >
                <div className="grid sm:grid-cols-2 h-full">
                  <div className="relative h-44 sm:h-auto overflow-hidden">
                    <img
                      src={getImage(featuredNews)}
                      alt={featuredNews.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-4 md:p-6 lg:p-8 flex flex-col justify-center">
                    <div className="flex items-center gap-2 mb-2.5 md:mb-4 flex-wrap">
                      {featuredNews.pinned && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-500 text-white text-xs font-semibold rounded-full">
                          <i className="ri-pushpin-fill text-xs"></i>置顶
                        </span>
                      )}
                      <span className="inline-block px-2.5 py-1 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full">
                        {featuredNews.category}
                      </span>
                    </div>
                    <h3 className="text-base md:text-xl lg:text-2xl font-bold text-gray-900 mb-2 md:mb-4 line-clamp-2 group-hover:text-teal-600 transition-colors">
                      {featuredNews.title}
                    </h3>
                    <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-6 line-clamp-3 leading-relaxed">
                      {getExcerpt(featuredNews)}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span>{getDate(featuredNews)}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>{getViews(featuredNews)}
                      </span>
                    </div>
                    <div className="mt-3 md:mt-5 flex items-center gap-1 text-xs text-teal-600 font-medium group-hover:gap-2 transition-all">
                      <span>阅读全文</span><i className="ri-arrow-right-line"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 侧边两篇 */}
            <div className="space-y-3 md:space-y-6">
              {sideNews.slice(0, 2).map((item) => (
                <div
                  key={getId(item)}
                  onClick={() => navigate(`/news/${getId(item)}`)}
                  className="bg-white rounded-xl md:rounded-2xl overflow-hidden hover:shadow-xl cursor-pointer group"
                >
                  <div className="relative h-32 sm:h-36 md:h-40 lg:h-44 overflow-hidden">
                    <img
                      src={getImage(item)}
                      alt={item.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-2.5 right-2.5 md:top-3 md:right-3 bg-white px-2 py-1.5 rounded-lg">
                      <div className="text-xs text-gray-500">{getDate(item).split('-')[1]}月</div>
                      <div className="text-sm font-bold text-gray-900">{getDate(item).split('-')[2]}</div>
                    </div>
                  </div>
                  <div className="p-3.5 md:p-5">
                    <span className="inline-block px-2.5 py-1 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full mb-2">
                      {item.category}
                    </span>
                    <h3 className="text-sm md:text-base font-bold text-gray-900 mb-1.5 line-clamp-2 leading-snug group-hover:text-teal-600 transition-colors">
                      {item.title}
                    </h3>
                    <div className="flex items-center justify-between text-xs text-gray-400 mt-2">
                      <span>{getDate(item)}</span>
                      <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{getViews(item)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* 底部更多文章 */}
            {sideNews.length > 2 && (
              <div className="lg:col-span-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {sideNews.slice(2).map((item) => (
                  <div
                    key={getId(item)}
                    onClick={() => navigate(`/news/${getId(item)}`)}
                    className="bg-white rounded-xl md:rounded-2xl overflow-hidden hover:shadow-xl cursor-pointer group"
                  >
                    <div className="relative h-40 md:h-48 overflow-hidden">
                      <img
                        src={getImage(item)}
                        alt={item.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-3.5 md:p-5">
                      <span className="inline-block px-2.5 py-1 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full mb-2 md:mb-3">
                        {item.category}
                      </span>
                      <h3 className="text-sm md:text-base font-bold text-gray-900 mb-1.5 line-clamp-2 group-hover:text-teal-600 transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed mb-2">{getExcerpt(item)}</p>
                      <div className="flex items-center justify-between text-xs text-gray-400">
                        <span>{getDate(item)}</span>
                        <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{getViews(item)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 无数据 */}
        {!loading && !featuredNews && (
          <div className="text-center py-20 text-gray-400">
            <i className="ri-newspaper-line text-4xl mb-3 block"></i>
            <p className="text-sm">暂无相关新闻</p>
          </div>
        )}

        {/* 查看更多 */}
        {!loading && newsList.length > 0 && (
          <div className="text-center mt-7 md:mt-12">
            <button
              onClick={() => navigate('/news/1')}
              className="inline-flex items-center px-6 py-2.5 border-2 border-gray-300 text-gray-700 text-xs md:text-sm font-medium rounded-full hover:border-teal-600 hover:text-teal-600 transition-all cursor-pointer"
            >
              查看更多新闻<i className="ri-arrow-right-line ml-2"></i>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
